import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { AccountConnectionStatus, IAccount } from '../../../models/account.models';
import { IFolderKPI, IInboxFolder, InboxFolderType } from '../../../models/inbox.models';
import { IUser as AuthUser } from 'src/app/features/auth/authentication/models/auth-response';
import { AppState } from 'src/app/core/app.state';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { MatDialog } from '@angular/material/dialog';
import { AccountSettingsDialogComponent, AccountSettingsTab } from '../account-settings-dialog/account-settings-dialog.component';
import { INBOX_FOLDER } from '../../../data/inbox.data';
import { ComposeService } from '../../../services/compose.service';
import * as smartInboxActions from '../../../store/smart-inbox.actions';
import { selectAccounts, selectActiveFolder, selectFilter, selectFoldersKpis } from '../../../store/smart-inbox.selectors';
import { IInboxFilter } from '../../../store/smart-inbox.reducers';

/**
 * Left-hand sidebar with compose button, account list, and system folders.
 * Owns account selection and dispatches it to the store; category/status/assignee
 * filtering lives in the conversation-list advanced filter panel.
 */
@Component({
  selector: 'app-inbox-sidebar',
  standalone: true,
  templateUrl: './inbox-sidebar.component.html',
  styleUrls: ['./inbox-sidebar.component.scss'],
  imports: [CommonModule, UiModule, SharedModule],
})
export class InboxSidebarComponent implements OnInit, OnDestroy {

  // ── Public properties ──────────────────────────────────────────────────────

  accounts: IAccount[] = [];
  /**
   * Tooltip text per account id, built when the accounts or the language change.
   *
   * Was a template getter calling `translate.instant()` per account per change-detection
   * tick; `instant()` also returns the raw key when translations have not loaded yet, so
   * a tick during startup could paint a key.
   */
  accountTooltips: Record<number, string> = {};

  foldersWithKpis: IInboxFolder[] = [...INBOX_FOLDER];
  foldersKPIs: IFolderKPI[] = [];
  activeFolder: InboxFolderType = InboxFolderType.ALL;
  activeAccounts: IAccount[] = [];

  isExpanded = true;
  currentUser: AuthUser | null = null;

  sectionCollapsed: Record<string, boolean> = {
    accounts: false,
  };


  readonly InboxFolderType = InboxFolderType;

  // ── Private properties ─────────────────────────────────────────────────────

  private currentFilter: IInboxFilter = {};
  private initialized = false;
  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private composeService: ComposeService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();

    this.store.select(selectActiveFolder).pipe(takeUntil(this.destroy$))
      .subscribe((folder) => { this.activeFolder = folder; });

    this.store.select(selectFoldersKpis).pipe(takeUntil(this.destroy$))
      .subscribe((foldersKPIs) => {
        this.foldersKPIs = foldersKPIs;
        this.foldersWithKpis = this.buildFoldersWithKpis();
      });

    this.store.select(selectFilter).pipe(takeUntil(this.destroy$))
      .subscribe((filter) => {
        this.currentFilter = filter;
        this.activeAccounts = filter.activeAccounts ?? [];
      });

    this.store.select(selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        const prevIds = this.accounts.map((a) => a.id).sort().join(',');
        this.accounts = accounts;
        this.rebuildAccountTooltips();
        const newIds = accounts.map((a) => a.id).sort().join(',');

        if (prevIds !== newIds) {
          this.reconcileActiveAccounts();
          if (this.initialized) {
            this.dispatchFilter();
          }
        }
      });

    // Tooltips are translated strings, so they are stale after a language switch.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.rebuildAccountTooltips());

    this.initialized = true;
    this.dispatchFilter();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Filter actions ────────────────────────────────────────────────────────

  toggleAccount(inbox: IAccount): void {
    if (this.activeAccounts.length === 0) {
      this.activeAccounts = [inbox];
    } else {
      const isActive = this.activeAccounts.some((i) => i.id === inbox.id);
      this.activeAccounts = isActive
        ? this.activeAccounts.filter((i) => i.id !== inbox.id)
        : [...this.activeAccounts, inbox];
    }
    this.dispatchFilter();
  }

  selectAllAccounts(): void {
    this.activeAccounts = [];
    this.dispatchFilter();
  }

  // ── Account actions ──────────────────────────────────────────────────────

  openAccountSettings(account: IAccount, initialTab: AccountSettingsTab = AccountSettingsTab.Details): void {
    this.dialog.open(AccountSettingsDialogComponent, {
      data: { account, initialTab },
      panelClass: 'account-settings-dialog-panel',
    });
  }

  // ── Connection alert ─────────────────────────────────────────────────────
  //
  // Surfaced on the connection dot next to the inbox name, not as a banner over the
  // list: the mail there is still readable and a banner offers nothing to act on.
  // Clicking the dot routes to the Status tab where Re-connect lives. The dot is the
  // single indicator for this mailbox's connection — there is deliberately no second
  // alert icon saying the same thing in the same row.

  /** Needs the user's attention to resume syncing. */
  hasConnectionAlert(account: IAccount): boolean {
    if (account.id === -1 || account.mailbox_type === 'shared') return false;
    return account.connection_status === AccountConnectionStatus.DISCONNECTED
      || account.connection_status === AccountConnectionStatus.ERROR;
  }

  /** i18n KEY; the template translates it. */
  connectionAlertTooltip(account: IAccount): string {
    const base = 'smart_inbox.inbox_view.sidebar_nav.';
    if (account.connection_status === AccountConnectionStatus.ERROR) {
      return `${base}connection_alert_error`;
    }
    // Non-owners cannot open the dialog, so tell them who can.
    return this.isOwnerOfAccount(account)
      ? `${base}connection_alert_disconnected`
      : `${base}connection_alert_disconnected_readonly`;
  }

  /**
   * Whether the dot is a control rather than a decoration. Only then does it get
   * a role, a tab stop and a label — an aria-hidden element you can click is
   * unreachable by keyboard and invisible to a screen reader.
   */
  isConnectionDotActionable(account: IAccount): boolean {
    return this.hasConnectionAlert(account) && this.isOwnerOfAccount(account);
  }

  onConnectionAlertClick(account: IAccount, event: Event): void {
    // Healthy mailbox: let the click through, so the dot behaves like the rest of the
    // row and toggles the account. This guard used to live in the template, when only
    // an alerting mailbox rendered anything clickable — the dot is on every row.
    if (!this.hasConnectionAlert(account)) return;

    // From here the click was aimed at the alert, so it is swallowed either way: a
    // non-owner must not toggle the mailbox filter by clicking it.
    event.stopPropagation();
    // Space would otherwise scroll the sidebar.
    event.preventDefault();

    if (!this.isOwnerOfAccount(account)) return;
    // Status is no longer its own tab — the connection section lives in Details.
    this.openAccountSettings(account, AccountSettingsTab.Details);
  }

  onAddNewAccount(): void {
    this.store.dispatch(smartInboxActions.AddNewAccount());
  }

  // ── UI actions ────────────────────────────────────────────────────────────

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  selectFolder(folder: InboxFolderType): void {
    if (this.activeFolder === folder) return;
    this.store.dispatch(smartInboxActions.SetActiveFolder({ folder }));
    this.store.dispatch(smartInboxActions.ClearActiveConversation());
  }

  openCompose(): void {
    this.composeService.openCompose();
  }

  // ── Template helpers ──────────────────────────────────────────────────────
  getFolderCounter(folder: IInboxFolder): number {
    if(folder.type === InboxFolderType.SENT || folder.type === InboxFolderType.TASKS || folder.type === InboxFolderType.ARCHIVED) return 0;
    
    if (folder.type === InboxFolderType.ALL || folder.type === InboxFolderType.MENTIONS) {
      return folder.unread_count;
    }
    
    return folder.total;
  }

  isAccountActive(account: IAccount): boolean {
    return this.activeAccounts.length > 0 && this.activeAccounts.some((i) => i.id === account.id);
  }

  isOwnerOfAccount(account: IAccount): boolean {
    return account.mailbox_owner_id === this.currentUser?.id;
  }

  /** Rebuilds every row's tooltip. Cheap: the account list is a handful of rows. */
  private rebuildAccountTooltips(): void {
    this.accountTooltips = this.accounts.reduce<Record<number, string>>((acc, account) => {
      acc[account.id] = this.buildAccountTooltip(account);
      return acc;
    }, {});
  }

  isAllAccountsSelected(): boolean {
    return this.activeAccounts.length === 0;
  }

  // ── Connection status ─────────────────────────────────────────────────────

  /**
   * Colour bucket for the connection dot. `pending` covers an account still
   * finishing its OAuth handshake; anything that is neither connected nor
   * pending is a problem the user must act on, so it reads as an error.
   */
  connectionStatusTone(account: IAccount): 'ok' | 'pending' | 'error' {
    switch (account.connection_status) {
      case AccountConnectionStatus.CONNECTED: return 'ok';
      case AccountConnectionStatus.PENDING: return 'pending';
      default: return 'error';
    }
  }

  /**
   * Two-line tooltip for an account row: the address, then its connection status.
   *
   * Deliberately one tooltip rather than one per element — the dot sits inside
   * the row, so separate tooltips opened together on hover.
   */
  private buildAccountTooltip(account: IAccount): string {
    const email = (account.email || '').trim();
    if (account.mailbox_type === 'shared') return email;

    // An alerting mailbox gets the actionable message ("Click to re-connect it");
    // a healthy one just states where it stands. One copy of each, never both.
    const statusKey = this.hasConnectionAlert(account)
      ? this.connectionAlertTooltip(account)
      : this.connectionStatusLabelKey(account);
    const status = this.translate.instant(statusKey);
    return email ? `${email}\n${status}` : status;
  }

  /**
   * Tooltip key for a mailbox with nothing to act on. Disconnected and errored
   * mailboxes are `connectionAlertTooltip`'s story — duplicating the wording here
   * is how the same fact ends up phrased two different ways.
   */
  private connectionStatusLabelKey(account: IAccount): string {
    const base = 'smart_inbox.inbox_view.sidebar_nav.connection_status.';
    return account.connection_status === AccountConnectionStatus.PENDING
      ? `${base}pending`
      : `${base}connected`;
  }

  // ── Private helpers ───────────────────────────────────────────────────────
  /** Translation key for folder label (used with translate pipe). */
  private getFolderLabelKey(folder: { type: InboxFolderType, label: string }): string {
    const keyByType: Record<string, string> = {
      [InboxFolderType.ALL]: 'smart_inbox.inbox_view.sidebar_nav.folder_all',
      [InboxFolderType.DRAFTS]: 'smart_inbox.inbox_view.sidebar_nav.folder_drafts',
      [InboxFolderType.SENT]: 'smart_inbox.inbox_view.sidebar_nav.folder_sent',
      [InboxFolderType.MENTIONS]: 'smart_inbox.inbox_view.sidebar_nav.folder_mentions',
    };
    return keyByType[folder.type] ?? folder.label;
  }

  /** Merges static folder config with store KPIs; used in subscription, not in template. */
  private buildFoldersWithKpis(): IInboxFolder[] {
    return INBOX_FOLDER.map((folder) => {
      const kpi = this.foldersKPIs.find((k) => k.id === folder.type);
      return { ...folder, label: this.getFolderLabelKey(folder), total: kpi?.total ?? 0, unread_count: kpi?.unread ?? 0 };
    });
  }

  /** Removes stale active accounts that no longer exist in the accounts list. */
  private reconcileActiveAccounts(): void {
    const nextAccounts = this.accounts ?? [];
    if (nextAccounts.length > 0) {
      this.activeAccounts = this.activeAccounts
        .map((a) => nextAccounts.find((n) => n.id === a.id))
        .filter((a): a is IAccount => a != null);
    }
  }

  private dispatchFilter(): void {
    // Preserve every other filter dimension (categories/status/assignee/advanced);
    // the sidebar only owns account selection now.
    const filter: IInboxFilter = {
      ...this.currentFilter,
      activeAccounts: this.activeAccounts,
    };
    this.store.dispatch(smartInboxActions.SetFilter({ filter }));
  }
}
